import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// SEC-003: tokens live ONLY in HttpOnly cookies managed by the backend.
// Any legacy keys from previous versions are wiped here, on login and on
// logout. The SPA must NEVER read/write tokens in localStorage.
const LEGACY_TOKEN_KEYS = ["icm_access_token", "icm_refresh_token"];
export const clearLegacyAuthStorage = () => {
  try {
    LEGACY_TOKEN_KEYS.forEach((k) => localStorage.removeItem(k));
    LEGACY_TOKEN_KEYS.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* storage disabled — nothing to do */ }
};
// Wipe on module load so cold starts clean up previous installs immediately.
clearLegacyAuthStorage();

const api = axios.create({
  baseURL: API,
  withCredentials: true,  // send/receive HttpOnly cookies
});

// No Authorization-header injection: the backend reads the access_token from
// the HttpOnly cookie. If an API client needs Bearer auth, it must craft the
// header itself — the SPA never caches tokens.

// Response interceptor: on 401, call /auth/refresh once (cookie-based) and
// retry the original request. If refresh fails, redirect to /login.
let refreshingPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    if (
      status === 401 &&
      original &&
      !original._retry &&
      !String(original.url || "").includes("/auth/refresh") &&
      !String(original.url || "").includes("/auth/login")
    ) {
      original._retry = true;
      try {
        if (!refreshingPromise) {
          refreshingPromise = axios
            .post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .then(() => true)
            .finally(() => { refreshingPromise = null; });
        }
        await refreshingPromise;
        return api(original);
      } catch (_e) {
        clearLegacyAuthStorage();
        const path = typeof window !== "undefined" ? window.location.pathname : "";
        const isPublicRoute = path.startsWith("/login") || path.startsWith("/join") || path.startsWith("/reset-password");
        if (typeof window !== "undefined" && !isPublicRoute) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Ha ocurrido un error. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const money = (v, cur = "USD") =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0);
export const money2 = (v, cur = "USD") =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: cur }).format(v || 0);

// Formato español DD/MM/YYYY
export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("es-ES");
  } catch { return iso; }
};

// Formato español DD/MM/YYYY HH:mm
export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};


// Descarga un archivo (PDF, Excel, etc.) desde el backend con auth y guarda como fichero
export async function downloadFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}
